import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Loader2, LogIn } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatDateTime } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import type { UserSessionsSnapshot } from '../types';
import SectionCard from './SectionCard';
import { formatDurationMs, sessionEndReasonLabel } from '../utils';
import {
  USER_SESSION_DAYS,
  useUserSessions,
  userSessionsDayLabel,
} from '../useUserSessions';
import {
  zoneLinkClass,
  zoneStatTileClass,
  zoneStatTileLabelClass,
  zoneTableHeaderClass,
  zoneTableRowClass,
  type OpsIntensity,
} from '../theme';

interface TodayUserLoginsCardProps {
  /** Initial today snapshot from full health payload (avoids extra fetch on first paint). */
  initialTodaySessions?: UserSessionsSnapshot;
  intensity?: OpsIntensity;
}

function userInitials(displayName: string | null, email: string): string {
  const source = (displayName || email).trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

const UserSessionsDayStrip: React.FC<{
  dayOffset: number;
  onSelect: (offset: number) => void;
}> = ({ dayOffset, onSelect }) => {
  return (
    <div
      className="flex gap-1.5 overflow-x-auto pb-1 -mx-0.5 px-0.5"
      role="tablist"
      aria-label="Select day"
    >
      {USER_SESSION_DAYS.map((day) => {
        const selected = dayOffset === day.offset;
        return (
          <button
            key={day.offset}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onSelect(day.offset)}
            title={day.fullLabel}
            className={cn(
              'flex min-w-[4.25rem] flex-col items-center rounded-xl border px-2 py-2 transition-all duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/60 focus-visible:ring-offset-1',
              selected
                ? 'border-violet-500 bg-violet-600 text-white shadow-md shadow-violet-500/25 scale-[1.02]'
                : day.isToday
                  ? 'border-violet-300/80 bg-violet-50/80 text-violet-900 hover:border-violet-400 dark:border-violet-600/60 dark:bg-violet-950/40 dark:text-violet-100'
                  : 'border-violet-200/60 bg-white/60 text-violet-900/80 hover:border-violet-300 hover:bg-violet-50/60 dark:border-violet-800/50 dark:bg-violet-950/20 dark:text-violet-100/90 dark:hover:bg-violet-950/35',
            )}
          >
            <span
              className={cn(
                'text-[10px] font-bold uppercase tracking-wide leading-none',
                selected ? 'text-violet-100' : 'text-violet-600/90 dark:text-violet-300/90',
              )}
            >
              {day.stripPrimary}
            </span>
            <span
              className={cn(
                'mt-1 text-lg font-bold tabular-nums leading-none',
                selected ? 'text-white' : 'text-violet-900 dark:text-violet-50',
              )}
            >
              {day.stripSecondary}
            </span>
          </button>
        );
      })}
    </div>
  );
};

type SummaryStatTone = 'violet' | 'good' | 'neutral';

const UserSessionsSummaryStrip: React.FC<{
  tiles: Array<{ key: string; label: string; value: number; tone: SummaryStatTone }>;
  isToday: boolean;
  dimmed?: boolean;
}> = ({ tiles, isToday, dimmed }) => {
  const { colors } = useTheme();

  const toneValueClass = (tone: SummaryStatTone) => {
    if (tone === 'good') return 'text-emerald-700 dark:text-emerald-300';
    if (tone === 'violet') return 'text-violet-800 dark:text-violet-200';
    return colors.text;
  };

  return (
    <div
      className={cn(
        'grid gap-2 shrink-0 transition-opacity duration-200',
        'lg:border-l lg:border-violet-200/60 lg:dark:border-violet-800/40 lg:pl-4',
        isToday ? 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3',
        dimmed && 'opacity-60',
      )}
    >
      {tiles.map((tile) => (
        <div
          key={tile.key}
          className={cn(
            zoneStatTileClass('people'),
            'bg-white/70 dark:bg-violet-950/30 min-w-[6.5rem]',
          )}
        >
          <p className={zoneStatTileLabelClass('people')}>{tile.label}</p>
          <p className={cn('text-xl font-bold tabular-nums mt-0.5', toneValueClass(tile.tone))}>
            {tile.value}
          </p>
        </div>
      ))}
    </div>
  );
};

const TodayUserLoginsCard: React.FC<TodayUserLoginsCardProps> = ({
  initialTodaySessions,
  intensity = 'admin',
}) => {
  const { colors } = useTheme();
  const [dayOffset, setDayOffset] = useState(0);
  const { data, isLoading, isValidating } = useUserSessions(dayOffset, initialTodaySessions);

  const selectedDayLabel = useMemo(() => userSessionsDayLabel(dayOffset), [dayOffset]);

  if (!data && isLoading) {
    return (
      <SectionCard
        zone="people"
        intensity={intensity}
        title="User logins"
        subtitle="Login, logout, and session duration"
      >
        <div className={cn('h-40 rounded-xl animate-pulse', colors.surfaceHover)} />
      </SectionCard>
    );
  }

  if (!data) return null;

  const { summary, sessions } = data;
  const isToday = dayOffset === 0;
  const isRefreshing = isValidating && !isLoading;

  const statTiles = [
    {
      key: 'sessions',
      label: isToday ? 'Sessions today' : 'Sessions',
      value: summary.totalSessions,
      tone: 'violet' as const,
    },
    {
      key: 'users',
      label: 'Unique users',
      value: summary.uniqueUsers,
      tone: 'neutral' as const,
    },
    ...(isToday
      ? [
          {
            key: 'active',
            label: 'Still active',
            value: summary.activeSessions,
            tone: 'good' as const,
          },
        ]
      : []),
    {
      key: 'ended',
      label: 'Ended',
      value: summary.endedSessions,
      tone: 'neutral' as const,
    },
  ];

  return (
    <SectionCard
      zone="people"
      intensity={intensity}
      title="User logins"
      subtitle="Login, logout, and session duration · server local time"
      action={
        <Link to="/activity-logs" className={zoneLinkClass('people')}>
          Activity logs →
        </Link>
      }
    >
      <div className="rounded-xl border border-violet-200/60 bg-violet-50/40 p-3 dark:border-violet-800/40 dark:bg-violet-950/25 mb-4">
        <div className="flex items-center justify-between gap-3 mb-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <CalendarDays className="h-4 w-4 shrink-0 text-violet-600 dark:text-violet-400" aria-hidden />
            <p className={cn('text-sm font-semibold truncate', colors.text)}>{selectedDayLabel}</p>
          </div>
          {isRefreshing && (
            <span className={cn('inline-flex items-center gap-1.5 text-xs shrink-0', colors.textMuted)}>
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              Updating
            </span>
          )}
        </div>
        <div
          className={cn(
            'flex flex-col gap-4 lg:flex-row lg:items-stretch',
            isRefreshing && 'opacity-60',
          )}
        >
          <div className="flex-1 min-w-0">
            <UserSessionsDayStrip dayOffset={dayOffset} onSelect={setDayOffset} />
          </div>
          <UserSessionsSummaryStrip tiles={statTiles} isToday={isToday} />
        </div>
      </div>

      {sessions.length === 0 ? (
        <div
          className={cn(
            'flex flex-col items-center justify-center rounded-xl border border-dashed py-10 px-4 text-center',
            'border-violet-200/70 bg-violet-50/30 dark:border-violet-800/40 dark:bg-violet-950/15 mt-4',
          )}
        >
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-violet-100 dark:bg-violet-900/50 mb-3">
            <LogIn className="h-5 w-5 text-violet-600 dark:text-violet-300" aria-hidden />
          </div>
          <p className={cn('text-sm font-medium', colors.text)}>No logins on {selectedDayLabel.toLowerCase()}</p>
          <p className={cn('text-xs mt-1 max-w-xs', colors.textMuted)}>
            Sessions appear here when users sign in during this calendar day.
          </p>
        </div>
      ) : (
        <div
          className={cn(
            'overflow-x-auto rounded-xl border border-violet-200/60 dark:border-violet-800/40 transition-opacity duration-200 mt-4',
            isRefreshing && 'opacity-60',
          )}
        >
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className={cn('px-3 py-2.5', zoneTableHeaderClass('people'))}>User</th>
                <th className={cn('px-3 py-2.5 hidden sm:table-cell', zoneTableHeaderClass('people'))}>Login</th>
                <th className={cn('px-3 py-2.5 hidden md:table-cell', zoneTableHeaderClass('people'))}>Logout</th>
                <th className={cn('px-3 py-2.5', zoneTableHeaderClass('people'))}>Duration</th>
                <th className={cn('px-3 py-2.5', zoneTableHeaderClass('people'))}>Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-violet-100 dark:divide-violet-900/40">
              {sessions.map((session, i) => (
                <tr
                  key={session.sessionId}
                  className={cn(
                    colors.text,
                    zoneTableRowClass('people', {
                      active: session.status === 'active',
                      zebra: i % 2 === 1,
                    }),
                    session.status !== 'active' && i % 2 === 1 && 'bg-violet-50/30 dark:bg-violet-950/10',
                  )}
                >
                  <td className="px-3 py-3 min-w-[12rem]">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={cn(
                          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                          session.status === 'active'
                            ? 'bg-emerald-100 text-emerald-800 ring-2 ring-emerald-400/40 dark:bg-emerald-900/50 dark:text-emerald-200'
                            : 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200',
                        )}
                        aria-hidden
                      >
                        {userInitials(session.displayName, session.email)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{session.displayName || session.email}</div>
                        <div className={cn('text-xs truncate', colors.textMuted)}>
                          {session.email}
                          <span className="ml-1.5 rounded bg-violet-100/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                            {session.role}
                          </span>
                        </div>
                        <div className={cn('text-xs mt-0.5 sm:hidden tabular-nums', colors.textMuted)}>
                          {formatDateTime(session.loginAt)}
                          {session.logoutAt ? ` → ${formatDateTime(session.logoutAt)}` : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap tabular-nums hidden sm:table-cell">
                    {formatDateTime(session.loginAt)}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap tabular-nums hidden md:table-cell">
                    {session.logoutAt ? formatDateTime(session.logoutAt) : '—'}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap tabular-nums font-semibold text-violet-900 dark:text-violet-100">
                    {formatDurationMs(session.durationMs)}
                    {session.status === 'active' && (
                      <span className={cn('ml-1 text-xs font-normal', colors.textMuted)}>so far</span>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {session.status === 'active' ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-2.5 py-1 text-xs font-semibold text-emerald-800 dark:text-emerald-200">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden />
                        Active
                      </span>
                    ) : (
                      <span
                        className={cn(
                          'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                          'bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-300',
                        )}
                      >
                        {sessionEndReasonLabel(session.endReason)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
};

export default TodayUserLoginsCard;
